/**
 * ColorPicker.js
 * Farbauswahl-Komponente: HSL/RGB Color Picker + Farbpalette.
 */

import React, { useState, useCallback } from 'react';

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function ColorPicker({ color = [0, 0, 0, 255], onChange, recentColors = [], onAddRecent }) {
  const [r, g, b, a] = color;
  const hexColor = rgbToHex(r, g, b);
  const [alphaPercent, setAlphaPercent] = useState(Math.round((a / 255) * 100));

  const handleHexChange = useCallback((e) => {
    const newRgb = hexToRgb(e.target.value);
    if (newRgb) {
      const newA = Math.round((alphaPercent / 100) * 255);
      onChange([...newRgb, newA]);
    }
  }, [alphaPercent, onChange]);

  const handleAlphaChange = useCallback((e) => {
    const newAlpha = parseInt(e.target.value, 10);
    setAlphaPercent(newAlpha);
    const newA = Math.round((newAlpha / 100) * 255);
    onChange([r, g, b, newA]);
  }, [r, g, b, onChange]);

  const handleRgbChange = useCallback((channel, value) => {
    const newColor = [r, g, b, a];
    newColor[channel] = Math.max(0, Math.min(255, parseInt(value) || 0));
    onChange(newColor);
  }, [r, g, b, a, onChange]);

  const handleColorPickerChange = useCallback((e) => {
    const newRgb = hexToRgb(e.target.value);
    const newA = Math.round((alphaPercent / 100) * 255);
    onChange([...newRgb, newA]);
    if (onAddRecent) onAddRecent([...newRgb, newA]);
  }, [alphaPercent, onChange, onAddRecent]);

  // Minecraft-typische Standardfarben
  const presetColors = [
    [0, 0, 0, 255],       // Schwarz
    [255, 255, 255, 255], // Weiss
    [170, 0, 0, 255],     // Dunkelrot
    [255, 0, 0, 255],     // Rot
    [255, 170, 0, 255],   // Gold
    [255, 255, 0, 255],   // Gelb
    [0, 170, 0, 255],     // Dunkelgruen
    [85, 255, 85, 255],   // Gruen
    [0, 170, 170, 255],   // Dunkelteal
    [85, 255, 255, 255],  // Aqua
    [0, 0, 170, 255],     // Dunkelblau
    [85, 85, 255, 255],   // Blau
    [170, 0, 170, 255],   // Dunkelmagenta
    [255, 85, 255, 255],  // Magenta
    [85, 85, 85, 255],    // Dunkelgrau
    [170, 170, 170, 255], // Hellgrau
    [139, 90, 43, 255],   // Braun (Erde)
    [255, 160, 120, 255], // Hautfarbe (hell)
    [210, 140, 90, 255],  // Hautfarbe (mittel)
    [120, 80, 40, 255],   // Hautfarbe (dunkel)
    [0, 0, 0, 0],         // Transparent
  ];

  return (
    <div className="color-picker-panel">
      {/* Farbvorschau + nativer Color Picker */}
      <div className="color-picker-preview-row">
        <div
          className="color-preview-box"
          style={{
            background: `rgba(${r},${g},${b},${a/255})`,
            border: '2px solid #555',
            width: 40,
            height: 40,
            borderRadius: 4,
            flexShrink: 0,
          }}
        />
        <input
          type="color"
          value={hexColor}
          onChange={handleColorPickerChange}
          style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
          title="Farbe waehlen"
        />
        <input
          type="text"
          value={hexColor}
          onChange={handleHexChange}
          className="mc-input"
          style={{ width: 80, fontFamily: 'monospace', fontSize: 13 }}
          maxLength={7}
        />
      </div>

      {/* RGB-Regler */}
      <div className="color-rgb-sliders">
        {['R', 'G', 'B'].map((ch, i) => (
          <div key={ch} className="color-channel-row">
            <label style={{ color: ['#ff6b6b', '#6bff6b', '#6b9fff'][i], minWidth: 16, fontWeight: 'bold' }}>{ch}</label>
            <input
              type="range" min="0" max="255" value={[r, g, b][i]}
              onChange={e => handleRgbChange(i, e.target.value)}
              style={{ flex: 1, accentColor: ['#ff6b6b', '#6bff6b', '#6b9fff'][i] }}
            />
            <input
              type="number" min="0" max="255" value={[r, g, b][i]}
              onChange={e => handleRgbChange(i, e.target.value)}
              className="mc-input"
              style={{ width: 46, textAlign: 'center', padding: '2px 4px' }}
            />
          </div>
        ))}
        {/* Alpha */}
        <div className="color-channel-row">
          <label style={{ color: '#aaa', minWidth: 16, fontWeight: 'bold' }}>A</label>
          <input
            type="range" min="0" max="100" value={alphaPercent}
            onChange={handleAlphaChange}
            style={{ flex: 1 }}
          />
          <input
            type="number" min="0" max="100" value={alphaPercent}
            onChange={handleAlphaChange}
            className="mc-input"
            style={{ width: 46, textAlign: 'center', padding: '2px 4px' }}
          />
        </div>
      </div>

      {/* Preset-Farben */}
      <div className="color-presets">
        <div className="color-presets-label">Voreinstellungen</div>
        <div className="color-presets-grid">
          {presetColors.map(([pr, pg, pb, pa], idx) => (
            <button
              key={idx}
              title={`rgba(${pr},${pg},${pb},${Math.round(pa/255*100)}%)`}
              className={`color-preset-btn ${r === pr && g === pg && b === pb && a === pa ? 'active' : ''}`}
              style={{
                background: pa === 0
                  ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, #fff 25%, #fff 75%, #ccc 75%)'
                  : `rgb(${pr},${pg},${pb})`,
                backgroundSize: pa === 0 ? '8px 8px' : undefined,
                backgroundPosition: pa === 0 ? '0 0, 4px 4px' : undefined,
              }}
              onClick={() => {
                onChange([pr, pg, pb, pa]);
                setAlphaPercent(Math.round(pa / 255 * 100));
              }}
            />
          ))}
        </div>
      </div>

      {/* Letzte Farben */}
      {recentColors.length > 0 && (
        <div className="color-presets">
          <div className="color-presets-label">Zuletzt benutzt</div>
          <div className="color-presets-grid">
            {recentColors.slice(0, 16).map(([pr, pg, pb, pa], idx) => (
              <button
                key={idx}
                className="color-preset-btn"
                style={{ background: `rgba(${pr},${pg},${pb},${pa/255})` }}
                onClick={() => {
                  onChange([pr, pg, pb, pa]);
                  setAlphaPercent(Math.round(pa / 255 * 100));
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ColorPicker;
